import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useFinpubReportFieldValuesLazyQuery } from "~/graphql/supergraphHooks";

export type FieldValueOption = {
  value: string;
  label: string;
};

type FieldValuesResult = {
  success?: boolean | null;
  message?: string | null;
  values?: unknown[] | null;
  pagination?: {
    page?: number | null;
    hasMore?: boolean | null;
  } | null;
};

type FieldValuesState = {
  options: FieldValueOption[];
  isLoading: boolean;
  isLoadingMore: boolean;
  hasLoaded: boolean;
  hasMore: boolean;
  error: Error | null;
  loadMore: () => Promise<void>;
};

const noopLoadMore = async () => {};

const EMPTY_FIELD_VALUES_STATE: FieldValuesState = {
  options: [],
  isLoading: false,
  isLoadingMore: false,
  hasLoaded: false,
  hasMore: false,
  error: null,
  loadMore: noopLoadMore,
};
const PAGE_LIMIT = 50;

type FieldValuesCacheEntry = {
  options: FieldValueOption[];
  nextPage: number;
  hasMore: boolean;
};

const cache = new Map<string, FieldValuesCacheEntry>();

export const clearReportQueryFieldValuesCache = () => {
  cache.clear();
};

const toOption = (value: unknown): FieldValueOption => {
  const label = String(value);
  return { value: label, label };
};

export const useReportQueryFieldValues = ({
  arenaId,
  field,
  search = null,
  enabled,
}: {
  arenaId: number | null | undefined;
  field: string | null;
  search?: string | null;
  enabled: boolean;
}): FieldValuesState => {
  // This hook owns pagination/cache state, so each page/search fetch should hit the backend.
  const [fetchFieldValuesPage] = useFinpubReportFieldValuesLazyQuery({
    fetchPolicy: "network-only",
  });
  const normalizedSearch = search?.trim() || null;
  const cacheKey = useMemo(() => {
    if (!enabled || !field) return null;
    return `${arenaId ?? "global"}:${field}:${normalizedSearch ?? ""}`;
  }, [arenaId, enabled, field, normalizedSearch]);

  const [state, setState] = useState<FieldValuesState>(EMPTY_FIELD_VALUES_STATE);
  const cacheKeyRef = useRef(cacheKey);
  cacheKeyRef.current = cacheKey;

  const fetchAndStorePage = useCallback(
    async ({
      page,
      isInitial,
      signal,
    }: {
      page: number;
      isInitial: boolean;
      signal: AbortSignal;
    }): Promise<void> => {
      if (!cacheKey || !field) return;
      const requestCacheKey = cacheKey;

      const response = await fetchFieldValuesPage({
        variables: {
          arenaId: arenaId ?? null,
          field,
          limit: PAGE_LIMIT,
          page,
          search: normalizedSearch ? [normalizedSearch] : null,
        },
        context: {
          fetchOptions: { signal },
        },
      });
      const result = response.data?.finpubReportFieldValues as FieldValuesResult | undefined;
      if (!result) throw new Error("Failed to fetch field values");
      if (result.success === false) {
        throw new Error(result.message ?? "Failed to fetch field values");
      }
      if (cacheKeyRef.current !== requestCacheKey) return;
      const incoming = (result.values ?? []).map(toOption);
      const nextPage = (result.pagination?.page ?? page) + 1;
      const hasMore = result.pagination?.hasMore === true;

      setState((current) => {
        const options = isInitial ? incoming : [...current.options, ...incoming];
        cache.set(cacheKey, { options, nextPage, hasMore });
        return {
          ...current,
          options,
          isLoading: false,
          isLoadingMore: false,
          hasLoaded: true,
          hasMore,
          error: null,
        };
      });
    },
    [arenaId, cacheKey, fetchFieldValuesPage, field, normalizedSearch],
  );

  useEffect(() => {
    if (!cacheKey || !field) {
      return undefined;
    }

    const cached = cache.get(cacheKey);
    if (cached) {
      setState({
        ...EMPTY_FIELD_VALUES_STATE,
        options: cached.options,
        hasLoaded: true,
        hasMore: cached.hasMore,
      });
      return undefined;
    }

    let isCurrent = true;
    const controller = new AbortController();
    setState({
      ...EMPTY_FIELD_VALUES_STATE,
      isLoading: true,
    });

    void fetchAndStorePage({ page: 1, isInitial: true, signal: controller.signal }).catch(
      (error: unknown) => {
        if (!isCurrent) return;
        if (error instanceof DOMException && error.name === "AbortError") return;
        setState({
          ...EMPTY_FIELD_VALUES_STATE,
          options: [],
          isLoading: false,
          isLoadingMore: false,
          hasLoaded: true,
          hasMore: false,
          error: error instanceof Error ? error : new Error("Failed to fetch field values"),
          loadMore: noopLoadMore,
        });
      },
    );

    return () => {
      isCurrent = false;
      controller.abort();
    };
  }, [arenaId, cacheKey, fetchAndStorePage, field]);

  const loadMore = useCallback(async (): Promise<void> => {
    if (!cacheKey || !field || state.isLoadingMore || !state.hasMore) return;
    const requestCacheKey = cacheKey;
    const cached = cache.get(cacheKey);
    const page = cached?.nextPage ?? 2;
    const controller = new AbortController();
    setState((current) => ({ ...current, isLoadingMore: true, error: null }));
    try {
      await fetchAndStorePage({ page, isInitial: false, signal: controller.signal });
    } catch (error: unknown) {
      if (error instanceof DOMException && error.name === "AbortError") return;
      if (cacheKeyRef.current !== requestCacheKey) return;
      setState((current) => ({
        ...current,
        isLoadingMore: false,
        error: error instanceof Error ? error : new Error("Failed to fetch field values"),
      }));
    }
  }, [cacheKey, fetchAndStorePage, field, state.hasMore, state.isLoadingMore]);

  return cacheKey ? { ...state, loadMore } : EMPTY_FIELD_VALUES_STATE;
};
